import { XIcon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import { useEffect } from 'react';
import { SWRResponse } from 'swr';

import { useAuth } from '@/hooks/useAuth/useAuth';

import { useFeedbacks } from '../../hooks/useSwr';
import {
  DatasetSchema,
  FeedbackConfigSchema,
  FeedbackSchema,
  RunSchema,
} from '../../types/schema';
import { Pane } from '../Pane';
import { RunAnnotationsCrudForm } from './RunAnnotationsCrudForm';
import { useSortedFeedbackConfigs } from './hooks/useSortedFeedbackConfigs';

export interface BaseProps {
  run?: RunSchema | null;
  dataset?: DatasetSchema;
  onSuccess?: () => void;
}

interface PaneProps extends BaseProps {
  isOpen: boolean;
  onClose: () => void;
  onFeedbackGiven?: () => void;
}

function RunAnnotationsCrudPaneContents({
  onFeedbackGiven,
  run,
  feedback,
  sessionId,
  userID,
  configs,
}: PaneProps & {
  feedback: SWRResponse<FeedbackSchema[]>;
  sessionId?: string;
  userID?: string;
  configs?: FeedbackConfigSchema[];
}) {
  // We use this component in the playground where sometimes the session_id is not available. Fetch the run data to get the true session_id first.
  return (
    <div
      className="flex grow flex-col bg-[var(--joy-palette-background-body)]"
      data-testid="run-annotations-crud-pane"
    >
      {feedback.data &&
      sessionId &&
      userID !== undefined &&
      configs &&
      !feedback.isValidating &&
      run ? (
        <RunAnnotationsCrudForm
          runId={run.id}
          sessionId={sessionId}
          runFeedbacks={feedback.data}
          userId={userID}
          onFeedbackGiven={onFeedbackGiven}
          configs={configs}
        />
      ) : (
        <div className="flex flex-col">
          <LinearProgress />
        </div>
      )}
    </div>
  );
}

export function RunAnnotationsCrudPane(props: PaneProps) {
  const { session: authSession, needsAuth } = useAuth();
  const userID = needsAuth ? authSession?.user?.id : null;
  const feedback = useFeedbacks({
    run: props.run?.id,
    session: props.run?.session_id,
    user: userID ?? undefined,
  });

  const { mutate: mutateFeedback } = feedback;

  // Revalidate feedback when the pane is opened to ensure that it's up to date with the backend
  useEffect(() => {
    if (props.isOpen) {
      mutateFeedback();
    }
  }, [mutateFeedback, props.isOpen]);

  const configs = useSortedFeedbackConfigs();

  return (
    <Pane
      open={props.isOpen}
      onClose={props.onClose}
      className="w-[450px] p-0"
      title="Annotate"
      titleClassName="min-h-[57px] py-3"
      noBackArrow={true}
      transparentBackdrop={true}
      topBarRightElement={
        <XIcon
          className="mr-5 h-5 w-5 cursor-pointer"
          onClick={props.onClose}
          data-testid="CloseIcon"
        />
      }
      dialogStyle={{
        marginLeft: 'calc(max(4.5rem, 100vw - max(450px)))',
        borderLeft: '1px solid rgba(var(--border-primary))',
      }}
    >
      {props.isOpen && (
        <RunAnnotationsCrudPaneContents
          {...props}
          onClose={props.onClose}
          feedback={feedback}
          sessionId={props.run?.session_id}
          userID={userID ?? undefined}
          configs={configs}
        />
      )}
    </Pane>
  );
}
